import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from "react-router";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/Spinner";
import { Get, Post } from "../api/api";
import { DEVICE_SHOP_ID_KEY, DEVICE_TOKEN_KEY } from "../api/device";
import logo from "../assets/logo_negro.png";
import background from "../assets/lowpoly.png";

const QR_POLL_INTERVAL = 5000;

export function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [qr, setQr] = useState(null);
  const [qrLoading, setQrLoading] = useState(true);
  const [qrError, setQrError] = useState("");
  const [now, setNow] = useState(Date.now());
  const initialQrRequested = useRef(false);

  const loadQrCode = useCallback(async () => {
    setQrLoading(true);
    setQrError("");

    try {
      let result = await Post({
        request: "/code/get",
        data: {
          device_token: localStorage.getItem(DEVICE_TOKEN_KEY) || "",
        },
      });

      if (result?.status === "invalid_device") {
        localStorage.removeItem(DEVICE_TOKEN_KEY);
        localStorage.removeItem(DEVICE_SHOP_ID_KEY);
        result = await Post({ request: "/code/get", data: { device_token: "" } });
      }

      if (result?.success && result.code && result.authorization_url) {
        setQr(result);
        setNow(Date.now());
      } else {
        setQr(null);
        setQrError(result?.message || "No se ha podido generar el código QR");
      }
    } catch (_error) {
      setQr(null);
      setQrError("No se ha podido conectar con el servidor");
    } finally {
      setQrLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialQrRequested.current) return;
    initialQrRequested.current = true;
    void loadQrCode();
  }, [loadQrCode]);

  useEffect(() => {
    if (!qr?.expires_at) return;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [qr?.expires_at]);

  useEffect(() => {
    if (!qr?.code) return;

    let active = true;
    let polling = false;

    const poll = async () => {
      if (polling) return;
      polling = true;

      try {
        const result = await Post({
          request: "/code/check",
          data: {
            code: qr.code,
            poll_token: qr.poll_token,
          },
        });

        if (!active) return;

        if (result?.token) {
          localStorage.setItem("token", result.token);
          localStorage.removeItem("exercise_id");

          if (result.device?.shop_id) {
            localStorage.setItem("shop_id", String(result.device.shop_id));
            localStorage.setItem(DEVICE_SHOP_ID_KEY, String(result.device.shop_id));
          } else if (result.shop_ids?.length) {
            localStorage.setItem("shop_id", String(result.shop_ids[0]));
          } else {
            localStorage.removeItem("shop_id");
          }

          navigate("/shopselect", { replace: true });
        } else if (["invalid", "expired", "forbidden"].includes(result?.status)) {
          setQr(null);
          setQrError(result.message || "El código QR ha caducado");
        } else if (result?.message) {
          setQr(null);
          setQrError(result.message);
        }
      } catch (_error) {
        // La siguiente iteración vuelve a intentarlo.
      } finally {
        polling = false;
      }
    };

    void poll();
    const interval = window.setInterval(poll, QR_POLL_INTERVAL);

    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [navigate, qr?.code, qr?.poll_token]);

  async function loginWithPassword(event) {
    event.preventDefault();
    if (loading) return;

    setLoading(true);
    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await Post({ request: "/login", data });

      if (response?.status === "success" && response.data?.token) {
        localStorage.setItem("token", response.data.token);
        localStorage.removeItem("shop_id");
        localStorage.removeItem("exercise_id");

        const profile = await Get({ request: "/user" }).catch(() => null);
        if (profile?.shops?.[0]?.id) {
          localStorage.setItem("shop_id", String(profile.shops[0].id));
        }
        navigate("/shopselect", { replace: true });
      } else {
        toast.error(response?.message || "Las credenciales no son correctas");
      }
    } catch (_error) {
      toast.error("No se ha podido conectar con el servidor");
    } finally {
      setLoading(false);
    }
  }

  const remainingSeconds = qr?.expires_at
    ? Math.max(0, Math.ceil((new Date(qr.expires_at).getTime() - now) / 1000))
    : 0;
  const remainingLabel = `${String(Math.floor(remainingSeconds / 60)).padStart(2, "0")}:${String(remainingSeconds % 60).padStart(2, "0")}`;

  return (
    <div className="relative min-h-screen overflow-auto p-4 sm:p-6">
      <div
        className="fixed inset-0"
        style={{ backgroundImage: `url(${background})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="fixed inset-0 bg-white/60 backdrop-blur-[2px]" />
      <div className="pointer-events-none fixed -left-24 top-10 h-72 w-72 rounded-full bg-emerald-300/25 blur-3xl" />
      <div className="pointer-events-none fixed -right-20 bottom-0 h-80 w-80 rounded-full bg-teal-300/25 blur-3xl" />

      <main className="relative z-10 mx-auto flex min-h-[calc(100vh-2rem)] w-full max-w-5xl items-center justify-center sm:min-h-[calc(100vh-3rem)]">
        <div className="grid w-full overflow-hidden rounded-[28px] border border-white/70 bg-white shadow-[0_30px_90px_-30px_rgba(5,78,69,0.45)] lg:grid-cols-[1.08fr_0.92fr]">
          <section className="relative flex min-h-[560px] flex-col overflow-hidden bg-gradient-to-br from-emerald-700 via-emerald-700 to-teal-800 px-6 py-7 text-white sm:px-10 sm:py-9">
            <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full border-[36px] border-white/5" />
            <div className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-teal-300/10 blur-2xl" />

            <div className="relative flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/20">
                <img src={logo} alt="AGest" className="h-7 w-7" />
              </div>
              <div>
                <div className="text-lg font-semibold tracking-tight">AGest</div>
                <div className="text-xs text-emerald-100/70">Punto de venta</div>
              </div>
            </div>

            <div className="relative mt-8 text-center sm:mt-10">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-emerald-50 ring-1 ring-white/10">
                <Icon icon="mdi:wifi" />
                Acceso rápido con QR
              </span>
              <h1 className="mx-auto mt-4 max-w-md text-3xl font-semibold tracking-tight sm:text-4xl">
                Conecta este TPV en segundos
              </h1>
              <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-emerald-50/80">
                Escanea el código con la cámara de tu móvil y autoriza el dispositivo desde el panel AGest.
              </p>
            </div>

            <div className="relative mx-auto mt-7 w-full max-w-[330px]">
              {qrLoading ? (
                <div className="flex aspect-square flex-col items-center justify-center rounded-3xl bg-white text-neutral-500 shadow-xl">
                  <Spinner />
                  <p className="mt-3 text-sm">Preparando acceso seguro...</p>
                </div>
              ) : qrError || !qr ? (
                <div className="flex aspect-square flex-col items-center justify-center rounded-3xl bg-white px-8 text-center text-neutral-700 shadow-xl">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500">
                    <Icon icon="mdi:alert-circle-outline" className="text-3xl" />
                  </div>
                  <p className="mt-4 text-sm font-medium">No se ha podido mostrar el QR</p>
                  <p className="mt-1 text-xs leading-5 text-neutral-500">{qrError}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-5 border-neutral-200"
                    onClick={loadQrCode}
                  >
                    <Icon icon="mdi:refresh" />
                    Reintentar
                  </Button>
                </div>
              ) : remainingSeconds === 0 ? (
                <div className="flex aspect-square flex-col items-center justify-center rounded-3xl bg-white px-8 text-center text-neutral-700 shadow-xl">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                    <Icon icon="mdi:qrcode-expired" className="text-3xl" />
                  </div>
                  <p className="mt-4 text-sm font-medium">El QR ha caducado</p>
                  <p className="mt-1 text-xs leading-5 text-neutral-500">Genera uno nuevo para continuar.</p>
                  <Button type="button" variant="outline" size="sm" className="mt-5" onClick={loadQrCode}>
                    <Icon icon="mdi:refresh" />
                    Generar nuevo QR
                  </Button>
                </div>
              ) : (
                <div className="rounded-3xl bg-white p-4 shadow-xl sm:p-5">
                  <div className="mx-auto w-fit rounded-2xl border border-neutral-100 bg-white p-3">
                    <QRCodeCanvas
                      value={qr.authorization_url}
                      size={220}
                      level="M"
                      marginSize={2}
                    />
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-3 text-xs">
                    <span className="flex items-center gap-2 font-medium text-emerald-700">
                      <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                      Esperando autorización
                    </span>
                    <span className="tabular-nums text-neutral-400">Expira en {remainingLabel}</span>
                  </div>
                  <div className="mt-2 truncate text-center font-mono text-[11px] tracking-[0.18em] text-neutral-400">
                    {qr.code}
                  </div>
                </div>
              )}

              {qr?.device && remainingSeconds > 0 && !qrError && (
                <div className="mt-4 flex items-center justify-center gap-3 rounded-2xl bg-black/20 px-4 py-3 text-left ring-1 ring-white/10">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10">
                    <Icon icon={qr.device.icon || "mdi:cash-register"} className="text-xl" />
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{qr.device.nombre}</div>
                    <div className="truncate text-xs text-emerald-100/70">
                      {[qr.device.location, qr.device.shop_name].filter(Boolean).join(" · ") || "Ubicación sin asignar"}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="flex items-center px-6 py-9 sm:px-10 sm:py-12 lg:px-12">
            <div className="mx-auto w-full max-w-sm">
              <img src={logo} alt="AGest" className="h-14 w-14 brightness-0 invert" />
              <div className="mt-7">
                <h2 className="text-2xl font-semibold tracking-tight text-neutral-900">También puedes entrar con tu cuenta</h2>
                <p className="mt-2 text-sm leading-6 text-neutral-500">
                  Usa el email con el que gestionas tu negocio.
                </p>
              </div>

              <form className="mt-7 space-y-4" onSubmit={loginWithPassword}>
                <div>
                  <label htmlFor="tpv-login-email" className="mb-1.5 block text-sm font-medium text-neutral-700">
                    Email
                  </label>
                  <Input
                    id="tpv-login-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="usuario@empresa.com"
                    className="h-12 rounded-xl bg-neutral-50/70 px-4 text-base"
                  />
                </div>
                <div>
                  <label htmlFor="tpv-login-password" className="mb-1.5 block text-sm font-medium text-neutral-700">
                    Contraseña
                  </label>
                  <PasswordInput
                    id="tpv-login-password"
                    name="password"
                    required
                    placeholder="••••••••"
                    className="h-12 rounded-xl bg-neutral-50/70 px-4 text-base"
                  />
                </div>
                <Button
                  type="submit"
                  loading={loading}
                  className="h-12 w-full rounded-xl bg-neutral-900 text-base hover:bg-neutral-800"
                >
                  {loading ? "Iniciando sesión..." : "Iniciar sesión"}
                  {!loading && <Icon icon="mdi:arrow-right" />}
                </Button>
              </form>

              <div className="mt-8 flex items-start gap-2 border-t border-neutral-100 pt-5 text-xs leading-5 text-neutral-400">
                <Icon icon="mdi:shield-check-outline" className="mt-0.5 shrink-0 text-emerald-600" />
                El acceso QR es la opción recomendada para vincular y controlar este TPV desde el panel.
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
